import type {FormEvent} from 'react';
import {useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';

import {appPaths} from '@/config/appPaths';
import {useAuth} from '@/hooks/useAuth';
import {authService} from '@/services/authService';
import type {AfriexTokenResponse} from '@/types';
import type {AfriexRoles} from '@/types/AfriexRoles';
import {parseJwt, saveAdminSession} from '@/utils/auth';
import {getErrorMessage} from '@/utils/errorMessage';
import {showErrorMessage} from '@/utils/showErrorMessage';
import {showSuccessMessage} from '@/utils/showSuccessMessage';

const useSetPassword = () => {
  const navigate = useNavigate();
  const {login} = useAuth();
  // The reset OTP hands its token over in navigation state; it becomes the
  // session only once the new password is saved.
  const {state} = useLocation() as {
    state: {tokenResponse?: AfriexTokenResponse} | null;
  };
  const tokenResponse = state?.tokenResponse;
  const [passwords, setPasswords] = useState({
    password: '',
    confirmPassword: '',
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    const {name, value} = event.target;
    setPasswords({...passwords, [name]: value});
  };

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    try {
      event.preventDefault();
      if (passwords.password !== passwords.confirmPassword) {
        throw new Error('Passwords do not match');
      }
      const token = tokenResponse?.tokens?.admin;
      if (!tokenResponse || !token) {
        throw new Error('Start the password reset again');
      }
      setIsLoading(true);
      await authService.updatePassword({password: passwords.password}, token);
      const adminRole = parseJwt(token)?.role as AfriexRoles;
      login(adminRole, saveAdminSession(tokenResponse, adminRole));
      showSuccessMessage('Password updated');
      setIsLoading(false);
      await navigate(`/${appPaths.users}`, {replace: true});
    } catch (error) {
      setIsLoading(false);
      showErrorMessage(
        getErrorMessage(error, 'Error updating password: Contact Engineering'),
      );
    }
  }

  return {
    tokenResponse,
    handleChange,
    handleSubmit,
    isLoading,
  };
};
export type SetPasswordFormProps = Omit<
  ReturnType<typeof useSetPassword>,
  'tokenResponse'
>;

export default useSetPassword;
